import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { DNode } from '@dojo/framework/core/interfaces';
import Button from '@dojo/widgets/button';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import has from '@dojo/framework/core/has';

import { numeral } from '@holmescorp/numeral';
import { postTestToolChange, userstore } from '@holmescorp/lcc-state';

import { logQuiz } from '@holmescorp/logger';
import { ScoreChart } from '@holmescorp/progress-chart';
import { publish } from '@holmescorp/pub-sub';

import { BaseProgression } from './base';
import { ScoreRollup } from '../support/score-rollup';
import { QuestionList } from '../support/questionlist';
import { Countdown } from '../support/countdown-timer';
import { CountdownTimer } from '../widgets/countdown-timer';
import { svc as pearsonvuesvc } from '../support/pearsonvue-svc';
import { svc as auditsvc } from '../support/audit-svc';
import { IQuestionFeedback } from '../support/qfsvc';
import {
	quizChange,
	infoChange,
	setTitle,
	setInreview,
	setIsaudit,
	setQuestion,
	setProgressionIsrunning
} from '../support/state';
import { delay } from '../support/delay';
import { QuizRPC, QuestionQueueType } from '../interfaces';

import css from '../css/prometric.m.css';

const isEmpty = (obj: any) => Object.keys(obj).length === 0;

@theme(css)
export class PearsonVue extends BaseProgression {
	protected svc: IQuestionFeedback = pearsonvuesvc;
	protected auditsvc = auditsvc;
	private countdown: Countdown | null = null;
	private isloading: boolean = false;

	protected onDetach(): void {
		this.countdown && this.countdown.stop();
		this.countdown = null;
	}

	protected async load_quiz(): Promise<boolean> {
		const { quizurl = '', quizstore } = this.properties;
		const { svc, auditsvc } = this;
		svc._smd!.url = quizurl;
		auditsvc._smd!.url = quizurl;
		if (this.isloading) {
			return true;
		}
		this.isloading = true;
		setProgressionIsrunning(quizstore)({ isrunning: '' });
		const [quiz, info] = await Promise.all([svc.quiz(), svc.info()]);
		const { title } = quiz;
		setTitle(quizstore)({ title });
		if (info.outoftime) {
			await svc.finish_quiz();
			await this.finalize();
			await delay(500);
			this.isloading = false;
			this.load_quiz();
		} else {
			quizChange(quizstore)({ quiz });
			infoChange(quizstore)({ info });
		}
		this.isloading = false;
		return true;
	}

	protected audit_startup(): Promise<boolean> {
		const {
			localizedBundle: {
				messages: { audit: auditText = 'Review' }
			},
			quiz,
			quizstore
		} = this.properties;

		setProgressionIsrunning(quizstore)({ isrunning: 'audit' });
		setTitle(quizstore)({ title: `${quiz ? quiz.title : ''} ${auditText}` });
		setQuestion(quizstore)({ question: undefined });
		this.invalidate();

		return Promise.resolve(true);
	}

	protected buttons(): DNode[] {
		const {
			localizedBundle: {
				messages: {
					continue: continueText = '',
					begin: beginText = '',
					review_last_attempt: reviewText = '',
					hide_begin_on_quiz_end = 'none'
				}
			},
			quiz = {} as QuizRPC,
			quizid,
			quizstore,
			info,
			isrunning,
			theme
		} = this.properties;
		const { use_restart } = quiz;
		const { svc } = this;
		const noBeginOnFinal = isrunning === 'final' && hide_begin_on_quiz_end !== 'none';

		if (use_restart) {
			if (this.countdown === null) {
				const { countdowntime } = info!;
				this.countdown = new Countdown(countdowntime, quizstore);
			}
			this.countdown && this.countdown.start();

			return [
				v('div', { xqa: 'action continue', classes: [this.theme(css.oneaction)] }, [
					w(
						Button,
						{
							theme,
							key: 'continue',
							onClick: () => {
								logQuiz.info(`continue quiz ${quizid}`);
								setInreview(quizstore)({ inreview: false });
								svc.continue_quiz().then((v) => {
									this.startquiz = v;
									this.quiz_startup();
								});
							}
						},
						[continueText]
					),
					v('div', { key: 'timer', classes: [this.theme(css.timerblock)] }, [
						v('div', { classes: [this.theme(css.timer)] }, [
							w(CountdownTimer, { quizstore, countdown: '' })
						])
					])
				])
			];
		} else {
			const lastresult = info && info.lastresult;
			const hasLastresult = lastresult && !isEmpty(lastresult);
			return [
				v('div', { xqa: 'action begin', classes: [this.theme(css.oneaction)] }, [
					noBeginOnFinal
						? null
						: w(
								Button,
								{
									theme,
									key: 'begin',
									onClick: () => {
										logQuiz.info(`begin quiz ${quizid}`);
										this.countdown && this.countdown.stop();
										this.countdown = null;
										setInreview(quizstore)({ inreview: false });
										svc.start_quiz().then((v) => {
											this.startquiz = v;
											this.quiz_startup();
										});
									}
								},
								[beginText]
								// tslint:disable-next-line:indent
						  )
				]),
				hasLastresult && !quiz.attempts_noaudit
					? w(
							Button,
							{
								theme,
								key: 'audit',
								onClick: () => {
									const { auditsvc } = this;
									logQuiz.info(`audit quiz ${quizid}`);

									setIsaudit(quizstore)({ isaudit: true });
									setInreview(quizstore)({ inreview: false });

									auditsvc.start_quiz().then(() => {
										this.startquiz = { cursor: 0, status: 'feedback' };
										this.audit_startup();
									});
								}
							},
							[reviewText]
							// tslint:disable-next-line:indent
					  )
					: null
			];
		}
	}

	protected render_micro_view(): DNode[] {
		const {
			quiz = {} as QuizRPC,
			info,
			isrunning,
			quizid,
			quizstore,
			theme,
			localizedBundle: {
				messages: {
					continue: continueText = '',
					takeText = 'Take Final Test',
					review_last_attempt: reviewText = '',
					revisit: revisitText = '',
					hide_begin_on_quiz_end = 'none',
					survey_url = '',
					survey_button_text = '',
					survey_text_final_test_passed = '',
					final_test_passed_text = '',
					final_test_score_label = 'Best Test Score'
				}
			}
		} = this.properties;
		const { svc } = this;
		const { use_restart } = quiz;

		const lastresult = info && info.lastresult;
		const hasLastresult = lastresult && !isEmpty(lastresult);
		const { instructions_done, instructions } = quiz;
		const noBeginOnFinal = isrunning === 'final' && hide_begin_on_quiz_end !== 'none';

		const bestScore = parseInt(info?.bestscore || '-1');
		const passingScore = parseFloat(quiz?.passingscore) || 80;
		const lastScore = hasLastresult ? lastresult!.totalpercentcorrect : 0;
		const hasPassed = hasLastresult ? lastScore >= passingScore : false;
		const { get, path } = userstore;
		const user_id = get(path('user_id'));
		const [first_name = '', last_name = ''] = get(path('fullname')).split(' ');
		const user_email = get(path('email'));

		const getHomeOrBackButton = (isHome: boolean) =>
			v(
				'div',
				{
					xqa: 'action home',
					classes: [this.theme(css.oneaction), isHome ? this.theme(css.primaryButton) : null]
				},
				[
					w(
						Button,
						{
							theme,
							key: 'home',
							onClick: () => {
								publish(`site/homepage`);
							}
						},
						[
							isHome
								? v('span', {}, [
										v('i', {
											classes: ['fas', 'fa-home', this.theme(css.actionIcon)]
										}),
										'Home'
								  ])
								: v('span', {}, [
										v('i', {
											classes: ['fas', 'fa-arrow-left', this.theme(css.actionIcon)]
										}),
										'Go Back'
								  ])
						]
					)
				]
			);

		const continue_button = v(
			'div',
			{
				xqa: 'action continue',
				classes: [this.theme(css.oneaction), this.theme(css.primaryButton)]
			},
			[
				w(
					Button,
					{
						theme,
						key: 'continue',
						onClick: () => {
							logQuiz.info(`continue quiz ${quizid}`);
							setInreview(quizstore)({ inreview: false });
							svc.continue_quiz().then((v) => {
								this.startquiz = v;
								this.quiz_startup();
							});
						}
					},
					[continueText]
				)
			]
		);

		const begin_button = v(
			'div',
			{
				xqa: 'action begin',
				classes: [this.theme(css.oneaction), !hasPassed ? this.theme(css.primaryButton) : null]
			},
			[
				noBeginOnFinal || hasPassed
					? null
					: w(
							Button,
							{
								theme,
								key: 'begin',
								disabled: hasPassed,
								onClick: () => {
									logQuiz.info(`begin quiz ${quizid}`);
									this.countdown && this.countdown.stop();
									this.countdown = null;
									setInreview(quizstore)({ inreview: false });
									svc.start_quiz().then((v) => {
										this.startquiz = v;
										this.quiz_startup();
									});
								}
							},
							[
								hasLastresult
									? revisitText
									: v('span', {}, [
											takeText,
											v('i', {
												classes: ['fas', 'fa-arrow-right', this.theme(css.actionIcon)]
											})
									  ])
							]
					  )
			]
		);

		const audit_button = v(
			'div',
			{
				xqa: 'action review',
				classes: [this.theme(css.oneaction), this.theme(css.primaryButton)]
			},
			[
				w(
					Button,
					{
						theme,
						key: 'audit',
						onClick: () => {
							const { auditsvc } = this;
							logQuiz.info(`audit quiz ${quizid}`);

							setIsaudit(quizstore)({ isaudit: true });
							setInreview(quizstore)({ inreview: false });

							auditsvc.start_quiz().then(() => {
								this.startquiz = { cursor: 0, status: 'feedback' };
								this.audit_startup();
							});
						}
					},
					[reviewText]
				)
			]
		);

		const survey_button = v(
			'div',
			{
				xqa: 'action survey',
				classes: [this.theme(css.oneaction), this.theme(css.primaryButton)]
			},
			[
				w(
					Button,
					{
						key: 'survey',
						onClick: () => {
							const surveyUrl = survey_url;
							if (surveyUrl) {
								postTestToolChange({ survey: { completed: true } });
								window.location.href = `${surveyUrl}/?principal_id=${user_id}&member_id=${user_id}&user_email=${user_email}&firstname=${first_name}&lastname=${last_name}&score=${lastScore}`;
							}
						}
					},
					[
						v('span', {}, [
							survey_button_text || 'Survey',
							v('i', {
								classes: ['fas', 'fa-arrow-right', this.theme(css.actionIcon)]
							})
						])
					]
				)
			]
		);

		const isFirstAttempt = !hasLastresult;
		const isAuditable = hasLastresult && !quiz.attempts_noaudit;
		const { survey } = get(path('toolstate', 'posttest')) || {};
		const surveyCompleted = survey?.completed || false;

		const instructionText =
			hasPassed && surveyCompleted
				? `${final_test_passed_text}`
				: hasPassed
				? `${instructions_done} ${survey_text_final_test_passed}`
				: hasLastresult && !use_restart
				? instructions_done
				: instructions;

		return [
			v('div', { classes: [this.theme(css.microQuizWrapper)], xqa: 'micro-quiz-wrapper' }, [
				v('div', { classes: [this.theme(css.microQuizHeader)] }, [
					v('div', {
						key: 'instructions',
						id: 'quiz-instructions',
						classes: [
							this.theme(css.instructions),
							hasPassed ? this.theme(css.passed_instructions) : null,
							'micro-quiz-instructions'
						],
						innerHTML: instructionText
					}),
					hasLastresult
						? v('div', { key: 'score', classes: [this.theme(css.score)], xqa: 'last-quiz-score' }, [
								w(ScoreChart, {
									score: lastresult?.totalpercentcorrect || -1,
									bestScore,
									bestScoreText: final_test_score_label,
									insideLabelText: 'Test Score'
								})
						  ])
						: v('div', { key: 'score', xqa: 'last-quiz-score' })
				]),
				v(
					'div',
					{
						key: 'quiz-actions',
						classes: [this.theme(css.actions)],
						role: 'group',
						...formatAriaProperties({ labelledby: 'quiz-instructions' })
					},
					use_restart
						? [getHomeOrBackButton(false), continue_button]
						: [
								isFirstAttempt ? getHomeOrBackButton(false) : null,
								noBeginOnFinal ? null : begin_button,
								isAuditable && hasPassed ? audit_button : null,
								hasPassed && !surveyCompleted ? survey_button : getHomeOrBackButton(true)
						  ]
				)
			])
		];
	}

	protected render_page(): DNode[] {
		const { isPlaceholder, messages } = this.properties.localizedBundle;
		if (isPlaceholder) {
			return [];
		}
		const scoreText = messages['score'] || '';
		const lastScoreText = messages['last_score'] || '';
		const { quiz = {} as QuizRPC, info, isrunning, quizstore } = this.properties;
		if (isrunning === 'audit') {
			const { startquiz, auditsvc } = this;
			const resultid = info && info.lastresult.id;
			const finalize = () => {
				setIsaudit(quizstore)({ isaudit: false });
				setInreview(quizstore)({ inreview: false });
				setTitle(quizstore)({ title: quiz.title });
				setProgressionIsrunning(quizstore)({ isrunning: '' });
				this.invalidate();
			};
			return [
				w<QuestionQueueType>(QuestionList, { svc: auditsvc as any, finalize, startquiz, resultid, quizstore })
			];
		} else if (isrunning === 'quiz') {
			if (this.countdown === null && !has('use-micro-view')) {
				const { countdowntime } = info!;
				this.countdown = new Countdown(countdowntime, quizstore);
			}
			this.countdown && this.countdown.start();
			const { startquiz, svc } = this;
			const finalize = () => {
				if (this.countdown !== null) {
					this.countdown.stop();
					this.countdown = null;
				}
				this.finalize();
			};
			return [w<QuestionQueueType>(QuestionList, { svc, finalize, startquiz, quizstore })];
		} else if (isrunning === 'final') {
			this.countdown && this.countdown.stop();
			this.countdown = null;

			const lastresult = info && info.lastresult;
			const hasLastresult = lastresult && !isEmpty(lastresult);
			const percent = hasLastresult ? lastresult!.totalpercentcorrect * 0.01 : 0;
			const { instructions_done } = quiz;

			if (has('use-micro-view') || has('use-quiz-survey')) {
				return this.render_micro_view();
			}

			return [
				hasLastresult ? w('QuizModuleProgress', { quizstore }) : null,
				v('div', {
					key: 'instructions',
					id: 'quiz-instructions',
					classes: [this.theme(css.instructions)],
					innerHTML: instructions_done
				}),
				hasLastresult
					? v('div', { key: 'score', classes: [this.theme(css.score)], xqa: 'last-quiz-score' }, [
							v('span', { classes: [this.theme(css.scoreTitle)] }, [`${scoreText} `]),
							v('span', { classes: [this.theme(css.scorePercent)] }, [`${numeral(percent).format('0%')}`])
					  ])
					: v('div', { key: 'score', xqa: 'last-quiz-score' }),
				v(
					'div',
					{
						key: 'quiz-actions',
						classes: [this.theme(css.actions)],
						role: 'group',
						...formatAriaProperties({ labelledby: 'quiz-instructions' })
					},
					this.buttons()
				)
			];
		} else {
			const { info } = this.properties;
			const lastresult = info && info.lastresult;
			const hasLastresult = lastresult && !isEmpty(lastresult);
			const percent = hasLastresult ? lastresult!.totalpercentcorrect * 0.01 : 0;
			const { instructions } = quiz;

			if (has('use-micro-view') || has('use-quiz-survey')) {
				return this.render_micro_view();
			}

			return [
				hasLastresult ? w('QuizModuleProgress', { quizstore }) : null,
				v('div', {
					key: 'instructions',
					id: 'quiz-instructions',
					classes: [this.theme(css.instructions)],
					innerHTML: instructions
				}),
				hasLastresult
					? v('div', { key: 'score', classes: [this.theme(css.score)], xqa: 'last-quiz-score' }, [
							v('span', { classes: [this.theme(css.scoreTitle)] }, [`${lastScoreText} `]),
							v('span', { classes: [this.theme(css.scorePercent)] }, [`${numeral(percent).format('0%')}`])
					  ])
					: v('div', { key: 'score', xqa: 'last-quiz-score' }),
				v(
					'div',
					{
						key: 'quiz-actions',
						classes: [this.theme(css.actions)],
						role: 'group',
						...formatAriaProperties({ labelledby: 'quiz-instructions' })
					},
					this.buttons()
				)
			];
		}
	}

	protected render(): DNode {
		const { quiz, info, key, quizstore } = this.properties;
		const hasdata = quiz && info;
		!hasdata && this.load_quiz();

		return v(
			'div',
			{ key, xqa: `progression pearson-vue` },
			hasdata
				? [w(ScoreRollup, { key: `rollup-${key}`, quizstore } as any, this.render_page())]
				: this.render_loading()
		);
	}
}

export default PearsonVue;
